import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import { useBodyScrollLock } from '../lib/bodyScrollLock';
import './Navbar.css';
import LanguageToggle from './LanguageToggle';

export default function Navbar() {
  const { t } = useLanguage();
  const [scrolled, setScrolled]             = useState(false);
  const [menuOpen, setMenuOpen]             = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [drawerSection, setDrawerSection]   = useState(null);
  const [notifTab, setNotifTab]             = useState('alerts');
  const [inboxTab, setInboxTab]             = useState('alerts');
  const [conversations, setConversations]   = useState([]);
  const [activeConvo, setActiveConvo]       = useState(null);
  const [messages, setMessages]             = useState([]);
  const [newMessage, setNewMessage]         = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const messagesEndRef = useRef(null);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { user, profile, logout, watchlist, notifications, markAllRead, unreadCount, removeFromWatchlist } = useAuth();

  const agentAvatarUrl = profile?.is_broker && profile?.avatar_url ? profile.avatar_url : null;
  const renderAvatar = (className) => (
    <div className={className}>
      {agentAvatarUrl
        ? <img src={agentAvatarUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        : user?.avatar}
    </div>
  );

  useEffect(() => {
    if (!user) return;
    fetchConversations();
    const channel = supabase
      .channel('navbar-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
        fetchConversations();
        if (activeConvo) fetchMessages(activeConvo.id);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations' }, () => {
        fetchConversations();
      })
      .subscribe();
    const poll = setInterval(fetchConversations, 30000);
    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [user?.id]);

  useEffect(() => {
    if (location.pathname === '/inbox' && user) fetchConversations();
  }, [location.pathname, user?.id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchConversations = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('conversations')
      .select(`
        *,
        listings(id, title, city),
        buyer:profiles!conversations_buyer_id_fkey(id, first_name, last_name, full_name, email),
        agent:profiles!conversations_agent_id_fkey(id, first_name, last_name, full_name, email)
      `)
      .or(`buyer_id.eq.${user.id},agent_id.eq.${user.id}`)
      .order('last_message_at', { ascending: false });
    if (data) {
      setConversations(data);
      const totalUnread = data.reduce((sum, c) => {
        if (c.buyer_id === user.id) return sum + (c.buyer_unread || 0);
        return sum + (c.agent_unread || 0);
      }, 0);
      setUnreadMessages(totalUnread);
    }
  };

  const fetchMessages = async (convoId) => {
    const { data } = await supabase
      .from('messages')
      .select('*, sender:profiles!messages_sender_id_fkey(id, first_name, last_name, full_name, email)')
      .eq('conversation_id', convoId)
      .order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const openConvo = async (convo) => {
    setActiveConvo(convo);
    await fetchMessages(convo.id);
    const field = convo.buyer_id === user.id ? 'buyer_unread' : 'agent_unread';
    await supabase.from('conversations').update({ [field]: 0 }).eq('id', convo.id);
    fetchConversations();
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvo) return;
    const content = newMessage.trim();
    setNewMessage('');
    await supabase.from('messages').insert({
      conversation_id: activeConvo.id,
      sender_id: user.id,
      content,
    });
    const isbuyer = activeConvo.buyer_id === user.id;
    await supabase.from('conversations').update({
      last_message: content,
      last_message_at: new Date().toISOString(),
      [isbuyer ? 'agent_unread' : 'buyer_unread']: supabase.rpc('increment', { x: 1 }),
    }).eq('id', activeConvo.id);
    fetchMessages(activeConvo.id);
    fetchConversations();
  };

  const resolveName = (p, fallback) =>
    p?.full_name?.trim() ||
    [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim() ||
    p?.first_name ||
    p?.email ||
    fallback;

  const getOtherPerson = (convo) => {
    if (!convo) return '';
    const isBuyer = convo.buyer_id === user?.id;
    return resolveName(isBuyer ? convo.agent : convo.buyer, isBuyer ? t('nav.agent') : t('nav.buyer'));
  };

  const getOtherFull = (convo) => getOtherPerson(convo);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return t('nav.justNow');
    if (diff < 3600000) return `${Math.floor(diff/60000)}${t('nav.minutesShort')}`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}${t('nav.hoursShort')}`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setActiveDropdown(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDrawerSection(null);
  }, [location.pathname]);

  // Drawer participates in the shared body-scroll lock so it can't desync
  // with other lockers (filter sheets, modals).
  useBodyScrollLock(menuOpen);

  const toggle        = (name) => setActiveDropdown(prev => prev === name ? null : name);
  const closeDrawer   = () => { setMenuOpen(false); setDrawerSection(null); };
  const toggleSection = (name) => setDrawerSection(prev => prev === name ? null : name);

  const NotifIcon = ({ icon }) => {
    if (icon === 'welcome') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
    if (icon === 'home') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
    if (icon === 'bell') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
    if (icon === 'star') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    if (icon === 'pin') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`} ref={navRef}>
        <div className="navbar__inner">

          <Link to="/" className="navbar__logo" aria-label="CosmoEstates home">
            <img src="/images/logo-transparent.png" alt="CosmoEstates" className="navbar__logo-img" />
          </Link>

          <nav className="navbar__nav">
            <Link to="/listings?type=sale" className={`navbar__navlink${location.pathname === '/listings' && location.search === '?type=sale' ? ' navbar__navlink--active' : ''}`}>{t('nav.buy')}</Link>
            <Link to="/listings?type=rent" className={`navbar__navlink${location.pathname === '/listings' && location.search === '?type=rent' ? ' navbar__navlink--active' : ''}`}>{t('nav.rent')}</Link>
            <Link to="/valuation" className={`navbar__navlink${location.pathname === '/valuation' ? ' navbar__navlink--active' : ''}`}>{t('nav.valuation')}</Link>
            <Link to="/find-agent" className={`navbar__navlink${location.pathname.startsWith('/find-agent') ? ' navbar__navlink--active' : ''}`}>{t('nav.findAgent')}</Link>
          </nav>

          <div className="navbar__actions">

            <Link to="/list-property" className="navbar__link navbar__link--cta">+ {t('nav.listProperty')}</Link>
            <LanguageToggle />

            {/* WATCHLIST */}
            <div className="navbar__icon-group">
              <button
                className={`navbar__icon-btn ${activeDropdown === 'watchlist' ? 'navbar__icon-btn--active' : ''}`}
                onClick={() => user ? toggle('watchlist') : navigate('/login', { state: { from: location.pathname } })}
              >
                <div className="navbar__icon-wrap">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  {user && watchlist.length > 0 && <span className="navbar__badge">{watchlist.length}</span>}
                </div>
                <span className="navbar__icon-label">{t('nav.watchlist')}</span>
              </button>
              {activeDropdown === 'watchlist' && user && (
                <div className="navbar__dropdown navbar__dropdown--watchlist">
                  <div className="dropdown__header">
                    <span className="dropdown__title">{t('nav.myWatchlist')}</span>
                    <span className="dropdown__count">{watchlist.length} {t('nav.savedSuffix')}</span>
                  </div>
                  <div className="dropdown__list">
                    {watchlist.length === 0 ? (
                      <div className="dropdown__empty">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.35}}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        <p>{t('nav.noSavedProperties')}</p>
                        <button onClick={() => { navigate('/listings'); setActiveDropdown(null); }}>{t('nav.browseListings')}</button>
                      </div>
                    ) : watchlist.map(w => (
                      <div key={w.id} className="watch-item" onClick={() => { navigate(`/property/${w.id}`); setActiveDropdown(null); }}>
                        <div className="watch-item__thumb" style={{ background: w.gradient }}>
                          {w.image ? <img src={w.image} alt={w.title} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'var(--radius-md)'}} /> : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.6}}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
                        </div>
                        <div className="watch-item__info">
                          <div className="watch-item__title">{w.title}</div>
                          <div className="watch-item__price">{w.priceType === 'rent' ? `€${w.price.toLocaleString()}/mo` : `€${w.price.toLocaleString()}`}</div>
                          <div className="watch-item__city"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> {w.city}</div>
                        </div>
                        <button className="watch-item__remove" onClick={e => { e.stopPropagation(); removeFromWatchlist(w.id); }}>&times;</button>
                      </div>
                    ))}
                  </div>
                  {watchlist.length > 0 && <div className="dropdown__footer"><button className="dropdown__footer-btn" onClick={() => { navigate('/saved'); setActiveDropdown(null); }}>{t('nav.viewAllSaved')}</button></div>}
                </div>
              )}
            </div>

            {/* PROFILE */}
            <div className="navbar__icon-group">
              <button
                className={`navbar__icon-btn ${activeDropdown === 'profile' ? 'navbar__icon-btn--active' : ''}`}
                onClick={() => toggle('profile')}
              >
                <div className="navbar__icon-wrap">
                  {user ? renderAvatar('navbar__avatar') : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  )}
                  {user && unreadMessages > 0 && (
                    <span className="navbar__badge navbar__badge--red">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
                  )}
                </div>
                <span className="navbar__icon-label">{user ? user.firstName : t('nav.profile')}</span>
              </button>
              {activeDropdown === 'profile' && (
                
                <div className="navbar__dropdown navbar__dropdown--profile">
                  {user ? (
                    <>
                      <div className="profile-user">
                        {renderAvatar('profile-user__avatar')}
                        <div className="profile-user__info">
                          <div className="profile-user__name">{user.firstName} {user.lastName}</div>
                          <div className="profile-user__email">{user.email}</div>
                        </div>
                      </div>
                      <div className="profile-menu">
                        <button className="profile-menu__item" onClick={() => { navigate('/profile'); setActiveDropdown(null); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg> {t('nav.myProfile')}</button>
                        <button className="profile-menu__item" onClick={() => { navigate('/my-listings'); setActiveDropdown(null); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> {t('nav.myListings')}</button>
                        <button className="profile-menu__item" onClick={() => { navigate('/inbox'); setActiveDropdown(null); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                          <span>Inbox</span>
                          {unreadMessages > 0 && (
                            <span className="profile-menu__badge">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
                          )}
                        </button>
                        <button className="profile-menu__item" onClick={() => { navigate('/settings'); setActiveDropdown(null); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> {t('nav.settings')}</button>
                        <div className="profile-divider" />
                        <button className="profile-menu__item profile-menu__item--logout" onClick={() => { logout().then(() => navigate("/")); setActiveDropdown(null); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg> {t('nav.logout')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="profile-options">
                      <button className="profile-option" onClick={() => { navigate('/login', { state: { from: location.pathname } }); setActiveDropdown(null); }}>
                        <div className="profile-option__icon profile-option__icon--login">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                        </div>
                        <div className="profile-option__text">
                          <div className="profile-option__label">{t('nav.signIn')}</div>
                          <div className="profile-option__sub">{t('nav.signInSub')}</div>
                        </div>
                      </button>
                      <div className="profile-divider" />
                      <button className="profile-option" onClick={() => { navigate('/register'); setActiveDropdown(null); }}>
                        <div className="profile-option__icon profile-option__icon--register">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                        </div>
                        <div className="profile-option__text">
                          <div className="profile-option__label">{t('nav.register')}</div>
                          <div className="profile-option__sub">{t('nav.registerSub')}</div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>

          <button className="navbar__hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* MOBILE DRAWER */}
      <div className={`drawer-backdrop ${menuOpen ? 'drawer-backdrop--visible' : ''}`} onClick={closeDrawer} />
      <div className={`drawer ${menuOpen ? 'drawer--open' : ''}`}>
        <div className="drawer__header">
          <span className="drawer__header-title">{t('nav.menu')}</span>
          <div className="drawer__header-actions">
            <LanguageToggle />
            <button className="drawer__close" onClick={closeDrawer}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {user && (
          <div className="drawer__userCard">
            {renderAvatar('drawer__userAvatar')}
            <div className="drawer__userText">
              <p className="drawer__userName">{`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}</p>
              <p className="drawer__userEmail">{user.email}</p>
            </div>
          </div>
        )}

        <nav className="drawer__nav">
          <Link
            to="/listings?type=sale"
            className={`drawer__navlink${location.pathname === '/listings' && location.search === '?type=sale' ? ' drawer__navlink--active' : ''}`}
            onClick={closeDrawer}
          >
            {t('nav.buy')}
          </Link>
          <Link
            to="/listings?type=rent"
            className={`drawer__navlink${location.pathname === '/listings' && location.search === '?type=rent' ? ' drawer__navlink--active' : ''}`}
            onClick={closeDrawer}
          >
            {t('nav.rent')}
          </Link>
          <Link
            to="/valuation"
            className={`drawer__navlink${location.pathname === '/valuation' ? ' drawer__navlink--active' : ''}`}
            onClick={closeDrawer}
          >
            {t('nav.valuation')}
          </Link>
        </nav>
        <div className="drawer__sep" />

        <div className="drawer__links">
          <Link to="/list-property" className="navbar__link navbar__link--cta drawer__link-full" onClick={closeDrawer}>+ {t('nav.listProperty')}</Link>
          <Link to="/find-agent" className="navbar__link navbar__link--outline drawer__link-full" onClick={closeDrawer}>{t('nav.findAgent')}</Link>
        </div>
        <div className="drawer__sep" />

        {/* WATCHLIST */}
        <button className="drawer__row" onClick={() => user ? toggleSection('watchlist') : (navigate('/login', { state: { from: location.pathname } }), closeDrawer())}>
          <div className="drawer__row-left">
            <div className="navbar__icon-wrap">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              {user && watchlist.length > 0 && <span className="navbar__badge">{watchlist.length}</span>}
            </div>
            <span className="drawer__row-label">{t('nav.watchlist')}</span>
          </div>
          <svg className={`drawer__chevron ${drawerSection === 'watchlist' ? 'drawer__chevron--open' : ''}`} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        {drawerSection === 'watchlist' && user && (
          <div className="drawer__sub">
            <div className="dropdown__header" style={{padding:'12px 16px 10px'}}>
              <span className="dropdown__title" style={{fontSize:13}}>{t('nav.myWatchlist')}</span>
              <span className="dropdown__count">{watchlist.length} {t('nav.savedSuffix')}</span>
            </div>
            {watchlist.length === 0
              ? <div className="dropdown__empty" style={{padding:'16px'}}><p>{t('nav.noSavedProperties')}</p><button onClick={() => { navigate('/listings'); closeDrawer(); }}>{t('nav.browseListings')}</button></div>
              : watchlist.map(w => (
                <div key={w.id} className="watch-item" style={{padding:'10px 16px'}} onClick={() => { navigate(`/property/${w.id}`); closeDrawer(); }}>
                  <div className="watch-item__thumb" style={{background:w.gradient}}>
                    {w.image ? <img src={w.image} alt={w.title} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'var(--radius-md)'}} /> : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.6}}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
                  </div>
                  <div className="watch-item__info">
                    <div className="watch-item__title">{w.title}</div>
                    <div className="watch-item__price">{w.priceType === 'rent' ? `€${w.price.toLocaleString()}/mo` : `€${w.price.toLocaleString()}`}</div>
                    <div className="watch-item__city">{w.city}</div>
                  </div>
                  <button className="watch-item__remove" onClick={e => { e.stopPropagation(); removeFromWatchlist(w.id); }}>&times;</button>
                </div>
              ))
            }
            {watchlist.length > 0 && <div className="dropdown__footer"><button className="dropdown__footer-btn" onClick={() => { navigate('/saved'); closeDrawer(); }}>{t('nav.viewAllSaved')}</button></div>}
          </div>
        )}

        <div className="drawer__sep" />

        {/* PROFILE — static menu for logged-in users, expandable sign-in/register for guests */}
        {user ? (
          <nav className="drawer__menu">
            <button className="drawer__menuItem" onClick={() => { navigate('/profile'); closeDrawer(); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              <span>{t('nav.myProfile')}</span>
            </button>
            <button className="drawer__menuItem" onClick={() => { navigate('/my-listings'); closeDrawer(); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              <span>{t('nav.myListings')}</span>
            </button>
            <button className="drawer__menuItem" onClick={() => { navigate('/inbox'); closeDrawer(); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <span>{t('nav.inbox')}</span>
              {unreadMessages > 0 && <span className="drawer__menuBadge">{unreadMessages}</span>}
            </button>
            <button className="drawer__menuItem" onClick={() => { navigate('/settings'); closeDrawer(); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              <span>{t('nav.settings')}</span>
            </button>
            <button className="drawer__menuItem drawer__menuItem--logout" onClick={() => { logout().then(() => navigate("/")); closeDrawer(); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              <span>{t('nav.logout')}</span>
            </button>
          </nav>
        ) : (
          <>
            <button className="drawer__row" onClick={() => toggleSection('profile')}>
              <div className="drawer__row-left">
                <div className="navbar__icon-wrap">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                </div>
                <span className="drawer__row-label">{t('nav.profile')}</span>
              </div>
              <svg className={`drawer__chevron ${drawerSection === 'profile' ? 'drawer__chevron--open' : ''}`} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {drawerSection === 'profile' && (
              <div className="drawer__sub">
                <div className="profile-options">
                  <button className="profile-option" onClick={() => { navigate('/login', { state: { from: location.pathname } }); closeDrawer(); }}>
                    <div className="profile-option__icon profile-option__icon--login"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg></div>
                    <div className="profile-option__text"><div className="profile-option__label">{t('nav.signIn')}</div><div className="profile-option__sub">{t('nav.signInSub')}</div></div>
                  </button>
                  <div className="profile-divider" />
                  <button className="profile-option" onClick={() => { navigate('/register'); closeDrawer(); }}>
                    <div className="profile-option__icon profile-option__icon--register"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg></div>
                    <div className="profile-option__text"><div className="profile-option__label">{t('nav.register')}</div><div className="profile-option__sub">{t('nav.registerSub')}</div></div>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
